import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Chip, Grid, Button, IconButton, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";

const PersonComment = ({ updateFormData, prevStep, handleSubmit, formData }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [incidentRecords, setIncidentRecords] = useState([]); // Stores submitted voice records
  const [note, setNote] = useState("");

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const handleSubmitRecord = async () => {
    // ✅ Prevent empty submission
    if (!note.trim() && !audioURL) {
      alert("Please add a note or record audio before submitting.");
      return;
    }

    try {
      let audioUrl = null;

      // ✅ Upload audio only if available
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const result = await uploadRes.json();
        audioUrl = result.audioUrl;
      }

      const newRecord = {
        behavior: note ? "Comment" : "Recording",
        audio: audioUrl,
        note: note.trim(),
      };

      const updatedRecords = [...incidentRecords, newRecord];
      setIncidentRecords(updatedRecords);
      updateFormData({ personCommentInfo: updatedRecords });

      // ✅ Reset form states
      setAudioURL(null);
      setNote("");
    } catch (err) {
      console.error("Audio upload or record submission failed:", err);
    }
  };

  // Handle Deleting a Record
  const handleDeleteRecord = (index) => {
    const updatedRecords = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updatedRecords);
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>
        Child and Young Person Debrief{" "}
      </Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black" }}>
            Child or Young Person’s comments
          </Typography>
          {/* Description */}
          <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
            Please can you put into words what you felt and how you feel about the physical intervention? (You can ask
            the staff member to write if you wish).
          </Typography>

          {/* Audio Recording UI */}
          <Box
            sx={{
              border: "2px dashed #ccc",
              padding: 3,
              textAlign: "center",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 180,
              mb: 2,
            }}
          >
            {!audioURL ? (
              <>
                <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>Try Recording Your Sound Now</Typography>
                <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                  To start recording press the <b>“Start Recording”</b> button and start recording!
                </Typography>
                <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                  {recording ? "Stop Recording" : "Start Recording"}
                </Button>
              </>
            ) : (
              <>
                <audio controls src={audioURL} />
                <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                  Record Again
                </Button>
              </>
            )}
          </Box>
          {/* Notes Field */}
          <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment</Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder='Enter your note here...'
            variant='outlined'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {(audioURL || note) && (
            <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={handleSubmitRecord}>
              Submit
            </Button>
          )}
        </Grid>
        <Grid item lg={4} md={4}>
          {/* Incident Management Details Section - Recorded Entries */}
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Incident Management
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Behavior Records</Typography>

            {incidentRecords.map((record, index) => (
              <Paper
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 1,
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid #ccc",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "black" }}>
                  {record.audio ? (
                    record.behavior
                  ) : (
                    <>
                      {record.behavior}
                      <br />
                      {record.note}
                    </>
                  )}
                </Typography>
                <Box>
                  {record.audio && (
                    <>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <MicIcon color='primary' />
                      </IconButton>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <CloudDownloadIcon color='primary' />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDeleteRecord(index)}>
                    <DeleteIcon color='error' />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          background: "#fff",
          padding: 2,
          zIndex: 10,
        }}
      >
        <Button variant='outlined' color='primary' onClick={prevStep}>
          Previous
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            handleSubmit(); // Call the function properly
          }}
          disabled={incidentRecords.length === 0}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default PersonComment;
